<template>
  <span
    v-click-outside="handleClickOutside"
    :class="mainClass"
  >
    <span
      class="dropdown-input__trigger"
      @click.stop="handleVisibility"
    >
      <button-input
        v-if="!hasDefaultSlot"
      >
        <font-awesome-icon
          :icon="triggerIcon"
          fixed-width
        />
      </button-input>
      <slot v-else />
    </span>
    <ul
      :class="classes"
    >
      <li
        v-for="option in options"
        :key="option.event"
        :class="option_classes(option)"
        @click.stop="handleSelect(option)"
      >
        <font-awesome-icon
          v-if="option.icon"
          :icon="option.icon"
          fixed-width
          class="dropdown__icon"
        />
        {{ option.label }}
      </li>
    </ul>
  </span>
</template>

<script>
import isBlank from 'is-blank'
import ButtonInput from '@/components/ui/ButtonInput'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

export default {
  name: 'DropdownInput',

  components: {
    ButtonInput,
    FontAwesomeIcon
  },

  props: {
    triggerIcon: {
      type: String,
      default: () => 'ellipsis-h'
    },
    position: {
      type: String,
      default: () => 'right',
      validator: value => ['left', 'right'].includes(value)
    },
    theme: {
      type: String,
      default: () => 'dark',
      validator: value => ['dark', 'light', 'primary'].includes(value)
    },
    full: {
      type: Boolean,
      default: () => false
    },
    options: {
      type: Array,
      required: true,
      validator: options => {
        options.forEach(option => {
          if (isBlank(option.value)) {
            throw new Error(`[DropdownInput] option is missing value: ${JSON.stringify(option)}`)
          }
          if (isBlank(option.label)) {
            throw new Error(`[DropdownInput] option is missing label: ${JSON.stringify(option)}`)
          }
        })
        return true
      }
    }
  },

  data() {
    return {
      isVisible: false
    }
  },

  computed: {
    hasDefaultSlot () {
      return !!this.$slots.default
    },
    mainClass () {
      return {
        'dropdown-input': true,
        'dropdown-input--full': this.full
      }
    },
    triggerIconClass () {
      return 'fa fa-ellipsis-h'
    },
    classes () {
      return {
        'dropdown-input__options': true,
        'dropdown-input__options--visible': this.isVisible,
        [`dropdown-input__options--${this.theme}`]: true,
        [`dropdown-input__options--${this.position}`]: true,
      }
    }
  },

  methods: {
    open () {
      this.isVisible = true
    },
    close () {
      this.isVisible = false
    },
    option_classes (option) {
      return {
        'dropdown-input__option': true,
        'dropdown-input__option--active': option.active === true
      }
    },
    handleVisibility () {
      this.isVisible = !this.isVisible
    },
    handleClickOutside () {
      if (this.isVisible) {
        this.close()
      }
    },
    handleSelect (option) {
      this.close()
      this.$emit('select', option)
    }
  }
}
</script>

<style lang="sass">
@import '~styles/imports'

.dropdown-input
  position: relative
  display: inline-block

.dropdown-input__trigger
  display: inline-block

.dropdown-input--full
    display: block

    .dropdown-input__trigger
      display: block

.dropdown-input__options
  +z-index('flyout')
  border-radius: rem-calc(3)
  border: 1px solid rgba(#fff, 0.15)
  box-shadow: $shadow-1dp
  left: 0
  margin-top: rem-calc(10)
  opacity: 0
  padding: rem-calc($grid-gutter-width-half $grid-gutter-width)
  position: absolute
  top: 100%
  transform: translateY(rem-calc(70))
  transition: $global-transition
  visibility: hidden
  width: rem-calc(240)

.dropdown-input__options--right
  left: auto
  right: 0

.dropdown-input__options--visible
  opacity: 1
  transform: translateY(0)
  visibility: visible

.dropdown-input__option
  +font-size(14, 14)
  border-bottom: 1px solid $color-border-2
  cursor: pointer
  display: block
  font-weight: 600
  padding: rem-calc(15 0)
  user-select: none
  white-space: nowrap

  &:hover,
  &:focus
    text-decoration: none

  &:last-child
    border-bottom: 0

  &--active
    font-weight: 900

.dropdown-input__options--dark
  background: $color-black
  color: $color-white

.dropdown-input__options--light
  background: $color-white
  color: $color-black

.dropdown-input__options--primary
  background: var(--dropdownBackground)
  color: $color-white

  .dropdown__option
    border-color: rgba($color-white, .5)

</style>



// WEBPACK FOOTER //
// src/components/ui/DropdownInput.vue